// WhatsAppChat.tsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChatInput from "./ChatInput";
import ChatWindow from "./ChatWindow";
import { SearchUsers } from "../apis/get";
import { useNavigate } from "react-router-dom";

interface Message {
    text: string;
    sender: "user" | "admin";
    type: string;
    timestamp: string;
    message_id?: string;
    is_read?: number | boolean;
    is_delivered?: number | boolean;
    _id?: string;
    wa_id?: string;
    from?: string;
    to?: string;
    status?: string;
    provider_response?: unknown;
    body?: string;
    caption?: string | null;
    media_url?: string | null;
    media_mime_type?: string | null;
    message_type?: string;
    target_message_id?: string;
    reactions?: Array<{
      emoji: string;
      wa_id?: string;
      reacted_at?: string;
      reaction_message_id?: string;
    }>;
    reply_to_message_id?: string | null;
    reply_to_wa_id?: string | null;
    is_forwarded?: boolean;
    is_frequently_forwarded?: boolean;
    reply_to_message?: Message;
}

export interface ChatUser {
    id?: number;
    wa_id: string;
    name: string;
    profile_name?: string | null;
    profile_pic_url?: string | null;
    country_code?: string | null;
    language_code?: string | null;
    source?: string | null;
    is_blocked?: boolean;
    is_active?: boolean;
    last_message_at?: string | null;
    last_incoming_at?: string | null;
    last_outgoing_at?: string | null;
    last_seen_at?: string | null;
    extra_data?: unknown;
    created_at?: string | null;
    updated_at?: string | null;
    last_message?: string;
    avatarUrl?: string;
    unread_count?: number;
    profile?: {
        userId?: string;
        name?: string;
        email?: string;
        phone?: string;
        gender?: string;
        age?: number;
        status?: string;
    } | null;
}


interface Props {
  messages: Message[];
  onSendMessage: (message: string) => void;
  user: ChatUser;
  onBack?: () => void;
}

const WhatsAppChat: React.FC<Props> = ({ messages, onSendMessage, user, onBack }) => {
  const chatRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [dbUser, setDbUser] = useState<any | null>(null);

  useEffect(() => {
    chatRef.current?.scrollTo(0, chatRef.current.scrollHeight);
  }, [messages]);

  useEffect(() => {
    const checkUserInDb = async () => {
      if (!user.wa_id) {
        setDbUser(null);
        return;
      }
      try {
        // WhatsApp numbers often include the country code '91' as a prefix.
        // We strip this prefix to search the database effectively.
        const normalizedSearchTerm = user.wa_id.startsWith('91') && user.wa_id.length > 10 
          ? user.wa_id.substring(2) 
          : user.wa_id;

        const response: any = await SearchUsers(normalizedSearchTerm);
        if (response && response.data && response.data.length > 0) {
          // Look for exact match in phone or ID using normalized value
          const found = response.data.find((u: any) => {
            const dbPhone = (u.phone || u.Phone || "").toString();
            return dbPhone.includes(normalizedSearchTerm) || 
                   (u.user_id?.toString() === normalizedSearchTerm) || 
                   (u.UserId?.toString() === normalizedSearchTerm) ||
                   (u.id?.toString() === normalizedSearchTerm);
          }) || response.data[0];
          
          setDbUser(found);
        } else {
          setDbUser(null);
        }
      } catch (error) {
        console.error("Error checking user in DB:", error);
        setDbUser(null);
      }
    };

    // checkUserInDb();
  }, []);

  const handleViewProfile = () => {
    if (!user.profile) return;
    
    const userId = user.profile.userId;
    const userData = {
      userId: userId?.toString(),
      name: user.profile.name || user.name ,
      email: user.profile.email,
      phone: user.profile.phone,
      gender: user.profile.gender,
      age: user.profile.age,
      status: user.profile.status
    };
    
    // Save to localStorage so UserDetails can load it correctly
    localStorage.setItem("userDetails", JSON.stringify(userData));
    navigate(`/users/user/${userId}`, { state: { from: 'whatsapp' } });
  };

  // console.log("messages", messages)
  return (
    <>
    <div className="flex flex-col h-full bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-md px-6 py-3.5 flex items-center space-x-5 shadow-sm border-b border-slate-800 z-20 sticky top-0">
        {/* Back button for mobile */}
        {onBack && (
          <button 
            onClick={onBack}
            className="md:hidden p-2 -ml-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        
        {/* Avatar */}
        <div className="relative cursor-pointer group">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover ring-2 ring-slate-800 group-hover:ring-blue-500 transition-all"
            />
          ) : (
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-slate-800 to-slate-700 flex items-center justify-center text-slate-300 font-bold text-lg ring-2 ring-slate-800 group-hover:ring-blue-500 transition-all">
              {user?.name?.substring(0, 1)?.toUpperCase()}
            </div>
          )}
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full"></div>
        </div>
        
        {/* User info */}
        <div className="flex-1 min-w-0 cursor-pointer">
          <div className="text-base font-bold text-white truncate">{user?.name}</div>
          <div className="text-xs text-slate-300 truncate flex items-center gap-1.5 flex-wrap">
             <span className="font-mono">+91 {user?.wa_id}</span>
             <span className="w-1 h-1 rounded-full bg-slate-600 inline-block"></span>
             <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
             <span>Online</span>
             
             {user.profile && (
               <button 
                 onClick={handleViewProfile}
                 className="ml-2 bg-blue-600/20 hover:bg-blue-600 border border-blue-500/30 text-blue-400 hover:text-white px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest transition-all"
                 title={`user_id: ${user.profile.userId} \nage: ${user.profile.age} \nstatus: ${user.profile.status} \ngender: ${user.profile.gender || 'N/A'}`}
               >
                 View Profile
               </button>
             )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center space-x-1 sm:space-x-3 text-slate-400">
          <button className="p-2.5 hover:bg-slate-800 hover:text-white rounded-xl transition-all duration-200" title="Search">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          <button className="p-2.5 hover:bg-slate-800 hover:text-white rounded-xl transition-all duration-200" title="Archive">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
             </svg>
          </button>
          <button className="p-2.5 hover:bg-slate-800 hover:text-white rounded-xl transition-all duration-200" title="More Options">
             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto px-4 py-6 sm:px-[5%] md:px-[7%] bg-slate-950 relative"
      >
        {/* Subtle Background Pattern (Optional CSS Grid/Dot) */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
             style={{ backgroundImage: 'radial-gradient(#64748b 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
        </div>

        <div className="relative z-10">
            <ChatWindow
            messages={messages}
            previewImage={previewImage}
            setPreviewImage={setPreviewImage}
            />
        </div>
      </div>

      {/* Input area */}
      <ChatInput onSend={onSendMessage} />
    </div>
    {previewImage && createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
        onClick={() => setPreviewImage(null)}
      >
        <button
          className="absolute top-5 right-5 text-white/80 hover:text-white bg-slate-800/80 hover:bg-slate-700 rounded-full p-2 transition-all shadow-xl"
          onClick={() => setPreviewImage(null)}
          aria-label="Close image preview"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img
          src={previewImage}
          alt="fullscreen"
          className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl shadow-2xl ring-1 ring-white/10"
          onClick={(e) => e.stopPropagation()}
        />
      </div>,
      document.body
    )}
    </>
  );
};

export default WhatsAppChat;
